import {
  Issue,
  Label,
  useCreateMutation,
  useCurrentWorkspace,
  useIssue,
  useWorkspaceLabels
} from '@gripp/shared-logic';
import {
  LoadingIndicator
} from '@gripp/shared-ui';
import { MultiItemSelectorModal } from 'libs/shared-ui/src/components/forms/multiItemSelectorModal';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ActivityEditLabels = ({
  issueId,
  selectedLabels,
  onLabelsChange,
  resetRef,
}: {
  issueId?: string;
  selectedLabels: Array<{ id: string; name: string }>;
  onLabelsChange: (labels: Array<{ id: string; name: string }>) => void;
  resetRef?: React.MutableRefObject<(() => void) | undefined>;
}) => {
  const { t } = useTranslation();

  const { issue: issueResponse, isLoading } = useIssue(issueId ?? '');
  const issue = issueResponse as Issue | undefined;

  const workspace = useCurrentWorkspace();
  const { labels: workspaceLabels = [] } = useWorkspaceLabels(
    issue?.workspace?.id ??
    workspace?.id
  );
  const { mutateAsync: createLabelMutation } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  const [search, setSearch] = useState('');
  const [createdLabels, setCreatedLabels] = useState<Label[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Sync parent state when issue data first loads or issue changes
  React.useEffect(() => {
    if (issue?.labels) {
      onLabelsChange(issue.labels);
      setCreatedLabels([]);
    }
  }, [issue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const allLabels = useMemo(() => {
    const combined = [...workspaceLabels, ...createdLabels];
    const map = new Map<string, Label>();
    combined.forEach((label) => {
      if (label?.id) {
        map.set(label.id, { id: label.id, name: label.name });
      }
    });
    return Array.from(map.values());
  }, [workspaceLabels, createdLabels]);

  const selectedLabelIds = useMemo(
    () => new Set(selectedLabels.map((label) => label.id)),
    [selectedLabels]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query
      ? allLabels.filter((label) => label.name.toLowerCase().includes(query))
      : allLabels;

    return base.map((label) => ({
      id: label.id,
      name: label.name,
      isActive: selectedLabelIds.has(label.id),
    }));
  }, [allLabels, search, selectedLabelIds]);

  const canCreateItem =
    !!search.trim() &&
    !allLabels.some(
      (label) => label.name.toLowerCase() === search.trim().toLowerCase()
    );

  const handleItemToggle = (item: Label) => {
    const isSelected = selectedLabelIds.has(item.id);
    if (isSelected) {
      onLabelsChange(selectedLabels.filter((label) => label.id !== item.id));
      return;
    }
    onLabelsChange([...selectedLabels, { id: item.id, name: item.name }]);
  };

  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      const toAdd = filteredItems
        .filter((item) => !selectedLabelIds.has(item.id))
        .map((item) => ({ id: item.id, name: item.name }));
      onLabelsChange([...selectedLabels, ...toAdd]);
    } else {
      const filteredIds = new Set(filteredItems.map((item) => item.id));
      onLabelsChange(selectedLabels.filter((l) => !filteredIds.has(l.id)));
    }
  };

  const handleCreateLabel = async (name: string): Promise<Label> => {
    const created = await createLabelMutation({
      input: {
        name,
        workspace: { id: issue?.workspace?.id },
      },
    });
    return created as Label;
  };

  const handleCreateItem = async () => {
    if (!search.trim() || !issue?.workspace?.id) {
      return;
    }

    setIsCreating(true);
    try {
      const newLabel = await handleCreateLabel(search.trim());
      setCreatedLabels((prev) => [...prev, newLabel]);
      onLabelsChange([...selectedLabels, newLabel]);
      setSearch('');
    } finally {
      setIsCreating(false);
    }
  };

  const onResetForm = () => {
    onLabelsChange(issue?.labels ?? []);
    setSearch('');
  };

  if (resetRef) {
    resetRef.current = onResetForm;
  }

  if (isLoading) {
    return (
      <LoadingIndicator />
    );
  }

  return (
    <MultiItemSelectorModal
      showOptions={true}
      search={search}
      setSearch={setSearch}
      searchPlaceholder={t('issue.common.searchLabel')}
      filteredItems={filteredItems}
      onItemToggle={handleItemToggle}
      onToggleAll={handleToggleAll}
      canCreateItem={canCreateItem}
      onCreateItem={handleCreateLabel}
      onCreatePress={handleCreateItem}
      isCreating={isCreating}
      createButtonText={t('common.create')}
    />
  );
};

export default ActivityEditLabels;

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) {
      id
      name
    }
  }
`;
